import { Text, Title } from "@clipboard-health/ui-components";
import { Box, DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useCallback, useEffect, useState } from "react";

export interface VideoPreviewScreenProps {
  videoUrl: string;
  durationSeconds: number;
  isUploading: boolean;
  workplaceId: string;
  onReRecord: () => void;
  onSubmit: () => void;
}

export function VideoPreviewScreen(props: VideoPreviewScreenProps) {
  const { videoUrl, durationSeconds, isUploading, workplaceId, onReRecord, onSubmit } = props;
  const [videoRef, setVideoRef] = useState<HTMLVideoElement>();

  const handleRef = useCallback((ref: HTMLVideoElement | null) => {
    setVideoRef(ref ?? undefined);
  }, []);

  useEffect(() => {
    videoRef?.load();
  }, [videoRef]);

  return (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
        }}
      >
        <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
          <Title variant="h3" component="h3" textAlign="center">
            Video saved
          </Title>
          <Text variant="body2" color="text.secondary" textAlign="center">
            Ready to submit.
          </Text>

          <Box
            sx={{
              width: "100%",
              "& video": {
                background: (theme) => theme.palette.common.black,
                width: "100%",
                maxHeight: "300px",
                display: "block",
                aspectRatio: "4 / 3",
                objectFit: "contain",
                borderRadius: (theme) => theme.shape.borderRadius,
              },
            }}
          >
            <video
              ref={handleRef}
              controls
              playsInline
              disablePictureInPicture
              disableRemotePlayback
              preload="metadata"
              src={videoUrl}
              onPlay={() => {
                logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_VIDEO_PREVIEW_PLAYED, {
                  workplaceId,
                  durationSeconds,
                });
              }}
              onEnded={() => {
                logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_VIDEO_PREVIEW_COMPLETED, {
                  workplaceId,
                  durationSeconds,
                });
              }}
            />
          </Box>

          <TextButton disabled={isUploading} onClick={onReRecord}>
            Re-record
          </TextButton>
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%" }}>
          <Text variant="caption" color="text.secondary">
            Reviews that use inappropriate language or references to specific patients or personnel
            will be removed.
          </Text>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isUploading}
            onClick={onSubmit}
          >
            Submit review
          </LoadingButton>
        </Stack>
      </DialogFooter>
    </>
  );
}
